import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Modal, Form, Table } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DeleteDialog from '../../components/DeleteDialog';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const Superadmin = () => {
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalForm, setHospitalForm] = useState({
    id: '',
    name: '',
    address: '',
    contactInfo: '',
    code: '',
  });
  const [adminForm, setAdminForm] = useState({
    name: '',
    email: '',
    password: '',
    hospitalId: '',
    hospitalName: '',
  });

  const [selectedHospital, setSelectedHospital] = useState(null);
  const [editAdminForm, setEditAdminForm] = useState({
    id: '',
    name: '',
    email: '',
    password: '',
    hospitalId: '',
    hospitalName: '',
  });
  const [adminError, setAdminError] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const fetchHospitals = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/superadmin/getHospitals`,
      );
      setHospitals(response.data.hospitals || []);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
    }
  };
  

  useEffect(() => {
    // Add an extra entry to browser history
    window.history.pushState(null, "", window.location.href);

    const handleBack = () => {
      window.history.go(1); // prevent back
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, []);

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitalDetails = async (hospitalId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/superadmin/getHospitalById/${hospitalId}`,
      );
      setSelectedHospital(response.data.hospital);
      setShowHospitalModal(true);
    } catch (error) {
      console.error('Error fetching hospital details:', error);
    }
  };

const handleAdminChange = (e) => {
  const { name, value } = e.target;

  setAdminForm({
    ...adminForm,
    [name]:
      name === "password"
        ? value.replace(/\s+/g, "") // ❌ password → no spaces allowed
        : value.replace(/^\s+/, ""), // ✔ name → allow middle, remove starting
  });
};



const handleEditAdminChange = (e) => {
  const { name, value } = e.target;

  setEditAdminForm({
    ...editAdminForm,
    [name]:
      name === "password"
        ? value.replace(/\s+/g, "") // ❌ password → no spaces allowed
        : value.replace(/^\s+/, ""), // ✔ name → allow middle, remove starting
  });
};



  const validateHospitalForm = () => {
    const newErrors = {};
    if (!hospitalForm.name.trim()) newErrors.name = 'Hospital name is required';
    if (!hospitalForm.address.trim()) newErrors.address = 'Address is required';
    if (!hospitalForm.contactInfo.trim()) {
      newErrors.contactInfo = 'Contact info is required';
    } else if (!/^\d{10}$/.test(hospitalForm.contactInfo)) {
      newErrors.contactInfo = 'Contact info must be a 10-digit number';
    }
    if (!hospitalForm.code.trim()) newErrors.code = 'Hospital code is required';
    setErrors(newErrors); // <-- Set errors state so UI shows errors
    return Object.keys(newErrors).length === 0;
  };

  const validateAdminForm = () => {
    const newErrors = {};
    if (!adminForm.name.trim()) newErrors.adminName = 'Admin name is required';
    if (!adminForm.email.trim()) {
      newErrors.adminEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(adminForm.email)) {
      newErrors.adminEmail = 'Email is invalid';
    }
    if (!adminForm.password.trim())
      newErrors.adminPassword = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddHospital = async () => {
    if (!validateHospitalForm()) return;
    try {
      await axios.post(`${BASE_URL}/api/superadmin/addHospital`, hospitalForm);
      console.log('Hospital added successfully');
      toast.success('Hospital added successfully');
      fetchHospitals();
      setShowHospitalModal(false);
      setErrors({}); // Clear errors after success
    } catch (error) {
      console.error('Error adding hospital:', error);
    }
  };

  const handleEditHospital = async () => {
    if (!validateHospitalForm()) return;
    try {
      if (!hospitalForm.id) {
        throw new Error('Hospital ID is missing');
      }
      await axios.put(
        `${BASE_URL}/api/superadmin/updateHospital/${hospitalForm.id}`,
        hospitalForm,
      );
      toast.success('Hospital updated successfully');
      fetchHospitals();
      setShowHospitalModal(false); // Close the modal
      setErrors({}); // Clear errors after success
    } catch (error) {
      console.error('Error updating hospital:', error);
    }
  };

  // Open the Edit Hospital Modal
  const openEditHospitalModal = (hospital) => {
    setHospitalForm({
      id: hospital.id,
      name: hospital.name,
      address: hospital.address,
      contactInfo: hospital.contact_info,
      code: hospital.code, // Add this line
    });
    setSelectedHospital(null); // Ensure selectedHospital is null to avoid opening details modal
    setShowHospitalModal(true); // Ensure the modal is opened for editing
  };

  const handleAddAdmin = async () => {
    if (!validateAdminForm()) return;
    try {
      setAdminError('');
      const response = await axios.post(
        `${BASE_URL}/api/superadmin/addAdmin`,
        adminForm,
      );
      toast.success('Admin added successfully');

      // Update selected hospital's admin list (if any)
      const newAdmin = response.data.admin;
      if (selectedHospital) {
        setSelectedHospital((prevHospital) => ({
          ...prevHospital,
          admins: [...prevHospital.admins, newAdmin],
        }));
      }

      // ✅ Clear the form
      setAdminForm({
        name: '',
        email: '',
        password: '',
        hospitalId: '',
        hospitalName: '',
      });

      setShowAdminModal(false);
      setErrors({});
    } catch (error) {
      console.error('Error adding admin:', error);
      if (error.response?.data?.message) {
        setAdminError(error.response.data.message);
      } else {
        setAdminError('An error occurred while adding the admin.');
      }
    }
  };

  const handleEditAdmin = async () => {
    try {
      await axios.put(
        `${BASE_URL}/api/superadmin/updateAdmin/${editAdminForm.id}`,
        editAdminForm,
      );
      toast.success('Admin updated successfully');
      fetchHospitals();
      fetchHospitalDetails(editAdminForm.hospitalId);
      setShowAdminModal(false);
    } catch (error) {
      console.error('Error updating admin:', error);
    }
  };

  const openAddHospitalModal = () => {
    setHospitalForm({
      id: '',
      name: '',
      address: '',
      contactInfo: '',
      code: '', // Add this line
    });
    setSelectedHospital(null); // Ensure selectedHospital is null to avoid opening details modal
    setShowHospitalModal(true);
    setErrors({}); // Clear errors when opening modal
  };

  const openHospitalDetailsPage = (hospitalId) => {
    navigate(`/hospitalDetails/${hospitalId}`);
  };

  const openAdminModal = (hospitalId, hospitalName) => {
    setAdminForm({
      name: '',
      email: '',
      password: '',
      hospitalId: hospitalId,
      hospitalName: hospitalName,
    });
    setEditAdminForm({
      id: '',
      name: '',
      email: '',
      password: '',
      hospitalId: '',
      hospitalName: '',
    });
    setShowAdminModal(true);
  };

  const handleInputNoSpace = (e, formSetter, formData) => {
  const { name, value } = e.target;
  formSetter({
    ...formData,
    [name]: value.replace(/\s/g, ''), 
  });
};

const preventSpace = (e) => {
  if (e.key === " ") {
    e.preventDefault();
  }
};
const handleInputAllowMiddleSpace = (e, setter, formState) => {
  const { name, value } = e.target;

  // ❌ starting spaces remove — ✔️ middle spaces allowed
  const cleanedValue = value.replace(/^\s+/, "");

  setter({
    ...formState,
    [name]: cleanedValue,
  });
};



  return (
    <Row>
      <Col md={12} className="px-5 py-3">
        {/* Heading and Add Hospital Button on the same line */}
        <Row className="mb-4">
          <Col
            xs={12}
            md={6}
            className="d-flex align-items-center custom-padding"
          >
            <h3 className="global-heading">Super Admin Dashboard</h3>
          </Col>
          <Col
            xs={12}
            md={6}
            className="d-flex justify-content-end custom-padding"
          >
            <Button
              onClick={openAddHospitalModal}
              className="global-btn"
              style={{
                backgroundColor: 'var(--primary-color)',
                border: 'none',
                padding: '8px 15px',
                fontSize: '0.9rem',
              }}
            >
              Add Hospital
            </Button>
          </Col>
        </Row>

        <div className="AdminTable-root table-responsive">
          <Table striped bordered hover className="w-100">
            <thead>
              <tr>
                <th>Sr.no.</th>
                <th>Hospital Name</th>
                <th>Hospital Code</th>
                <th>Address</th>
                <th>Contact</th>
                <th>Action</th>
                <th style={{ width: '10%' }}>Add Admin</th>
              </tr>
            </thead>
            <tbody>
              {hospitals.map((hospital, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    <button
                      
                      onClick={() => openHospitalDetailsPage(hospital.id)}
                      className="p-0"
                      style={{
                        color: 'var(--primary-color)',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                      }}
                    >
                      {hospital.name}
                    </button>
                  </td>
                  <td>{hospital.code}</td>
                  <td>{hospital.address}</td>
                  <td>{hospital.contact_info}</td>
                  <td>
                    <button
                      onClick={() => openEditHospitalModal(hospital)}
                      className="border-0 fw-semibold primary-class bg-transparent "
                            
                    >
                     <span className="material-icons me-1" style={{ fontSize: 16 }}>
                                edit
                              </span>
                    </button>
                    <DeleteDialog
                      id={hospital.id}
                      path={`${BASE_URL}/api/superadmin/deleteHospital/${hospital.id}`}
                      message="Hospital deleted successfully"
                      icon="custom"
                      onSuccess={fetchHospitals}
                    />
                  </td>
                  <td>
                    <Button
                      onClick={() => openAdminModal(hospital.id, hospital.name)}
                      className="btn-custom"
                    >
                      Add Admin
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Add/Edit Hospital Modal */}
        <Modal
          show={showHospitalModal && !selectedHospital}
          onHide={() => setShowHospitalModal(false)}
          className="form-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {hospitalForm.id ? 'Edit Hospital' : 'Add Hospital'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Render form for Add/Edit Hospital */}
            <Form>
              <Form.Group controlId="hospitalName">
                <Form.Label>Hospital Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={hospitalForm.name || ''}
 onChange={(e) =>
    handleInputAllowMiddleSpace(e, setHospitalForm, hospitalForm)
  }                  placeholder="Hospital Name"
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="hospitalAddress">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={hospitalForm.address || ''}
onChange={(e) =>
    handleInputAllowMiddleSpace(e, setHospitalForm, hospitalForm)
  }                  placeholder="Address"
                  isInvalid={!!errors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="hospitalContactInfo">
                <Form.Label>Contact Info</Form.Label>
                <Form.Control
                  type="text"
                  name="contactInfo"
                  value={hospitalForm.contactInfo || ''}
                 onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/\D/g, ""); // only digits
                    if (onlyNumbers.length <= 10) {   // ⬅️ allow max 10 digits
                      setHospitalForm({
                        ...hospitalForm,
                        contactInfo: onlyNumbers,
                      });
                    }
                  }}
                  placeholder="Contact Info"
                  isInvalid={!!errors.contactInfo}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.contactInfo}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="hospitalCode">
                <Form.Label>Hospital Code</Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={hospitalForm.code || ''}
                  onChange={(e) =>
                      handleInputAllowMiddleSpace(e, setHospitalForm, hospitalForm)
                    }                  placeholder="Hospital Code"
                  isInvalid={!!errors.code}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.code}
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={hospitalForm.id ? handleEditHospital : handleAddHospital}
              className="btn-custom"
            >
              {hospitalForm.id ? 'Edit Hospital' : 'Add Hospital'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Hospital Details Modal */}
        <Modal
          show={showHospitalModal && selectedHospital}
          onHide={() => setShowHospitalModal(false)}
          className="form-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedHospital ? selectedHospital.name : ''} Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedHospital && (
              <>
                <h5>Hospital Details</h5>
                <p>
                  <strong>Name:</strong> {selectedHospital.name}
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {selectedHospital && (
              <DeleteDialog
                id={selectedHospital.id}
                path={`${BASE_URL}/api/superadmin/deleteHospital/${selectedHospital.id}`}
                message="Hospital deleted successfully"
                onSuccess={() => {
                  fetchHospitals();
                  setShowHospitalModal(false);
                }}
              />
            )}
          </Modal.Footer>
        </Modal>

        {/* Admin Add/Edit Modal */}
        <Modal
          show={showAdminModal}
          onHide={() => setShowAdminModal(false)}
          className="form-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editAdminForm.id ? 'Edit Admin' : 'Add Admin'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {adminError && <p className="text-danger">{adminError}</p>}
            <Form>
              <Form.Group controlId="adminName">
                <Form.Label>Admin Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={editAdminForm.id ? editAdminForm.name : adminForm.name}
                  onChange={
                    editAdminForm.id ? handleEditAdminChange : handleAdminChange
                  }
                  placeholder="Admin Name"
                  isInvalid={!!errors.adminName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.adminName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="adminEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  autoComplete="off"
                  value={editAdminForm.id ? editAdminForm.email : adminForm.email}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (editAdminForm.id) {
                      setEditAdminForm({
                        ...editAdminForm,
                        email: value.replace(/\s/g, ""), // ❌ remove all spaces
                      });
                    } else {
                      setAdminForm({
                        ...adminForm,
                        email: value.replace(/\s/g, ""), // ❌ remove all spaces
                      });
                    }
                  }}
                  onKeyDown={preventSpace} // block typing space
                  placeholder="Admin Email"
                  isInvalid={!!errors.adminEmail}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.adminEmail}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="adminPassword">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={
                    editAdminForm.id
                      ? editAdminForm.password
                      : adminForm.password
                  }
                  onChange={
                    editAdminForm.id ? handleEditAdminChange : handleAdminChange
                  }
                  placeholder="Admin Password"
                  isInvalid={!!errors.adminPassword}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.adminPassword}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="hospitalId">
                <Form.Label>Hospital</Form.Label>
                <Form.Control
                  as="select"
                  name="hospitalId"
                  value={
                    editAdminForm.id
                      ? editAdminForm.hospitalId
                      : adminForm.hospitalId
                  }
                  onChange={
                    editAdminForm.id ? handleEditAdminChange : handleAdminChange
                  }
                  disabled
                >
                  {hospitals.map((hospital) => (
                    <option key={hospital.id} value={hospital.id}>
                      {hospital.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={editAdminForm.id ? handleEditAdmin : handleAddAdmin}
              className="btn-custom"
            >
              {editAdminForm.id ? 'Edit Admin' : 'Add Admin'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Col>
    </Row>
  );
};

export default Superadmin;
